import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, RouterButtonLink } from "../components/Button";
import { Card } from "../components/Card";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { Container } from "../components/Container";
import { FormActions, FormActionsStart } from "../components/FormActions";
import { InfoBox } from "../components/InfoBox";
import { LoadingContainer } from "../components/LoadingContainer";
import { PageDetailHeader } from "../components/PageDetailHeader";
import { useCatalogScope } from "../hooks/useCatalogScope";
import { CATALOG_NEW_ITEM_ID, useCatalogNav } from "./catalog/catalogNav";
import { BookkeepingAccountForm } from "./components/BookkeepingAccountForm";
import {
  useGetBookkeepingAccountQuery,
  useCreateBookkeepingAccountMutation,
  useUpdateBookkeepingAccountMutation,
  useDeleteBookkeepingAccountMutation,
} from "../query/bookkeepingAccountsQuery";
import type { RestCreateBookkeepingAccount } from "@KassaroAB/shared/types/bookkeepingAccount";

const FORM_ID = "catalog-bookkeeping-account-form";

export const BookkeepingAccountFormView = () => {
  const { t } = useTranslation();
  const { setDirty, allowNavigate } = useCatalogNav();
  const { scope, pagePath } = useCatalogScope("bookkeepingAccounts");
  const { itemId } = useParams<{ itemId: string }>();
  const isCreating = itemId === CATALOG_NEW_ITEM_ID;
  const accountId = !isCreating && itemId ? itemId : null;

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [savedTick, setSavedTick] = useState(0);

  const {
    data: account,
    isLoading,
    error: loadError,
  } = useGetBookkeepingAccountQuery(scope, accountId);
  const {
    mutate: createMutation,
    error: createError,
    isPending: isCreatePending,
  } = useCreateBookkeepingAccountMutation(scope);
  const {
    mutate: updateMutation,
    error: updateError,
    isPending: isUpdatePending,
  } = useUpdateBookkeepingAccountMutation(scope);
  const {
    mutate: deleteMutation,
    error: deleteError,
    isPending: isDeletePending,
  } = useDeleteBookkeepingAccountMutation(scope);

  const isSaving = isCreatePending || isUpdatePending;
  const missing = !isCreating && !!accountId && !isLoading && !account;

  const handleSubmit = (data: RestCreateBookkeepingAccount) => {
    if (isCreating) {
      createMutation(data, {
        onSuccess: (created) => {
          allowNavigate(`${pagePath}/${created.id}`);
        },
      });
      return;
    }
    if (!accountId) return;
    updateMutation(
      { accountId, account: data },
      {
        onSuccess: () => {
          setSuccessMessage(t("bookkeepingAccountsView.successUpdated"));
          setSavedTick((tick) => tick + 1);
        },
        onError: () => setSuccessMessage(null),
      },
    );
  };

  const confirmDelete = () => {
    if (!accountId) return;
    deleteMutation(accountId, {
      onSuccess: () => {
        setDeleteOpen(false);
        allowNavigate(pagePath);
      },
    });
  };

  useEffect(() => {
    if (!successMessage) return;
    const timer = setTimeout(() => setSuccessMessage(null), 3000);
    return () => clearTimeout(timer);
  }, [successMessage]);

  const title = isCreating
    ? t("bookkeepingAccountsView.panelCreate")
    : (account?.name ?? t("bookkeepingAccountsView.title"));
  const breadcrumbCurrent = isCreating
    ? t("bookkeepingAccountsView.panelCreate")
    : missing
      ? t("catalog.itemNotFound")
      : (account?.name ?? t("common.loading"));

  if (!scope) {
    return null;
  }

  const showForm = isCreating || (!!account && !missing);

  return (
    <Container>
      <PageDetailHeader
        title={title}
        parentLabel={t("bookkeepingAccountsView.title")}
        parentTo={pagePath}
        currentLabel={breadcrumbCurrent}
      />
      {loadError && (
        <InfoBox variant="error">
          {t("bookkeepingAccountsView.errorLoad")}
        </InfoBox>
      )}
      {(createError || updateError || deleteError) && (
        <InfoBox variant="error">
          {t("bookkeepingAccountsView.errorSave")}
        </InfoBox>
      )}
      {successMessage && (
        <InfoBox variant="success">{successMessage}</InfoBox>
      )}
      <Card>
        {!isCreating && isLoading && !account ? (
          <LoadingContainer message={t("bookkeepingAccountsView.loading")} />
        ) : missing || !showForm ? (
          <InfoBox variant="error">{t("catalog.itemNotFound")}</InfoBox>
        ) : (
          <>
            <BookkeepingAccountForm
              key={account?.id ?? CATALOG_NEW_ITEM_ID}
              formId={FORM_ID}
              account={isCreating ? undefined : account}
              savedTick={savedTick}
              onSubmit={handleSubmit}
              onDirtyChange={setDirty}
            />
            <FormActions>
              {!isCreating && (
                <FormActionsStart>
                  <Button
                    type="button"
                    $color="danger"
                    onClick={() => setDeleteOpen(true)}
                    disabled={isSaving || isDeletePending}
                  >
                    {t("common.delete")}
                  </Button>
                </FormActionsStart>
              )}
              <RouterButtonLink
                to={pagePath}
                $color="secondary"
                onClick={(event) => {
                  if (isSaving) event.preventDefault();
                }}
              >
                {t("common.cancel")}
              </RouterButtonLink>
              <Button type="submit" form={FORM_ID} disabled={isSaving}>
                {isSaving
                  ? t("common.saving")
                  : isCreating
                    ? t("common.create")
                    : t("common.update")}
              </Button>
            </FormActions>
          </>
        )}
      </Card>
      <ConfirmDialog
        isOpen={deleteOpen}
        title={t("bookkeepingAccountForm.deleteAccount")}
        message={t("bookkeepingAccountsView.deleteConfirm", {
          name: account?.name ?? "",
        })}
        confirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
        danger
        onConfirm={confirmDelete}
        onCancel={() => setDeleteOpen(false)}
      />
    </Container>
  );
};
